import React from 'react';
import Snackbar from '@material-ui/core/Snackbar';
import { SnackbarContentWrapper } from './Toast.style';

/**
 * @component
 * @description Custom toast notification component with success/error styling.
 * Provides consistent notification display with customizable appearance and positioning.
 *
 * Key Features:
 * - **Success/Error States**: Different colors for success and error messages
 * - **Auto-Hide**: Configurable auto-hide duration with default 3 seconds
 * - **Top-Right Positioning**: Professional positioning in top-right corner
 * - **Custom Styling**: Uses styled wrapper for enhanced appearance
 * - **Flexible Duration**: Customizable display duration
 * - **Manual Control**: Programmatic open/close control
 * - **Accessibility**: Proper ARIA attributes for screen readers
 *
 * Visual Features:
 * - **Color Coding**: Blue (#3498db) for success, Red (#e74c3c) for errors
 * - **Professional Positioning**: Top-right corner for non-intrusive display
 * - **Smooth Animations**: Built-in Material-UI slide animations
 * - **Consistent Styling**: Uses themed styled components
 * - **Message Display**: Clear, readable message text
 *
 * Interaction Features:
 * - **Auto-Hide**: Automatically dismisses after specified duration
 * - **Manual Close**: Can be closed programmatically via handleClose
 * - **Click Outside**: Supports click-outside-to-close behavior
 * - **Escape Key**: Keyboard accessibility for closing
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the toast is visible
 * @param {string} props.message - The message text to display
 * @param {boolean} [props.success] - Whether this is a success message (affects color)
 * @param {number} [props.duration=3000] - Auto-hide duration in milliseconds
 * @param {Function} props.handleClose - Function to call when toast closes
 *
 * @returns {JSX.Element} Toast notification component
 *
 * @example
 * // Success toast
 * <Toast
 *   open={showSuccessToast}
 *   message="Operation completed successfully!"
 *   success={true}
 *   handleClose={() => setShowSuccessToast(false)}
 * />
 *
 * @example
 * // Error toast with custom duration
 * <Toast
 *   open={showErrorToast}
 *   message="Something went wrong. Please try again."
 *   success={false}
 *   duration={5000}
 *   handleClose={() => setShowErrorToast(false)}
 * />
 *
 * @example
 * // Usage in form submission
 * const handleSubmit = async (data) => {
 *   try {
 *     await submitForm(data);
 *     setToastMessage("Form submitted successfully!");
 *     setToastSuccess(true);
 *     setShowToast(true);
 *   } catch (error) {
 *     setToastMessage("Failed to submit form");
 *     setToastSuccess(false);
 *     setShowToast(true);
 *   }
 * };
 *
 * return (
 *   <>
 *     <form onSubmit={handleSubmit}>
 *       // form content
 *     </form>
 *     <Toast
 *       open={showToast}
 *       message={toastMessage}
 *       success={toastSuccess}
 *       handleClose={() => setShowToast(false)}
 *     />
 *   </>
 * );
 *
 * @example
 * // Usage with toast context
 * const { showToast } = useToast();
 *
 * const handleAction = () => {
 *   showToast("Action completed!", true); // message, isSuccess
 * };
 *
 * @see Snackbar - Material-UI Snackbar component
 * @see SnackbarContentWrapper - Custom styled wrapper component
 * @see React.Fragment - React fragment for message content
 */
function Toast(props) {
    return (
        <Snackbar
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right'
            }}
            autoHideDuration={props.duration || 3000}
            open={props.open}
            onClose={props.handleClose}>
            <SnackbarContentWrapper
                className={'themed'}
                style={{ background: props.success ? '#3498db' : '#e74c3c' }}
                aria-describedby="client-snackbar"
                classes={{
                    message: 'notification-message'
                }}
                message={<React.Fragment>{props.message}</React.Fragment>}
            />
        </Snackbar>
    );
}

export default Toast;
